import React, { useCallback, useMemo, useRef, useState } from 'react';
import { TableCycleData } from '../types';
import getTempByMeasurement from '../../../utils/getTempByMeasurement';
import { DeltaView } from './DeltaView';
import CycleMeasurement from '../../../enums/cycle-measurement';
import { CycleTableItemState } from './CycleTableItemState/CycleTableItemState';
import { BG_STATE_COLORS_ZEBRA } from '../constants';
import { useTranslation } from 'react-i18next';
import { ChannelState } from '../../../enums/channel-state.enum';
import CycleStatus from '../../../enums/cycle-status.enum';
import CycleSettings from '../../../enums/cycle-setting.enum';
import ChannelSettings from '../../ChannelSettings/ChannelSettings';
import { Form, Formik } from 'formik';
import InteractiveButton from '../../InteractiveButton/InteractiveButton';
import { ElementSize } from '../../../types/element-size';
import { ChangeChannelAction } from '../../../enums/action';
import { changeChannelActionWithReturn } from '../../../pages/EditController/SubPages/EditChannel/channelActions';
import InlineEditableCell from './InlineEditableCell';
import MeasuredCurrentModal from '../../MeasuredCurrentModal/MeasuredCurrentModal';

export interface ICycleTableRowProps {
  device: TableCycleData;
  measurement: CycleMeasurement;
  index: number;
  controllerFaulted?: boolean;
  testChannel?: (controllerId: string, itemNumber: number) => void;
  canEditChannels?: boolean;
  onDataChange?: (device: TableCycleData, updates: Partial<TableCycleData>) => void;
}

const RowItem = ({ children, classExtend }: { children?: React.ReactNode; classExtend?: string }) => {
  return <td className={`c-cycle-table__td py-1 ${classExtend ? classExtend : ''}`}>{children}</td>;
};

export const CycleTableRow = ({
  device,
  measurement,
  index,
  controllerFaulted,
  testChannel,
  canEditChannels,
  onDataChange,
}: ICycleTableRowProps) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [showCurrentModal, setShowCurrentModal] = useState(false);
  const [currentAtClick, setCurrentAtClick] = useState<number | null>(null);

  const targetRef = useRef<HTMLParagraphElement | null>(null);
  const getBackgroundColor = () => {
    const shade = index % 2 === 0 ? 'light' : 'dark';
    const zebraColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-2';

    if (device.iotState === ChannelState.STOPPED || device.status !== CycleStatus.Active) return zebraColor;

    if (controllerFaulted) return BG_STATE_COLORS_ZEBRA[ChannelState.FAULTED][shade];

    return BG_STATE_COLORS_ZEBRA[device.iotState]?.[shade] || zebraColor;
  };

  const { t } = useTranslation();

  const handleHover = useCallback(async () => {
    if (!device?.isChannel) return;

    const rect = targetRef.current?.getBoundingClientRect();
    if (rect) {
      setPopupPosition({
        top: rect.top + window.scrollY + rect.height,
        left: rect.left + window.scrollX,
      });
    }

    setShowPopup(true);
  }, [device]);

  const onMouseLeaveHandler = useCallback(async () => {
    setShowPopup(false);
    setPopupPosition({
      top: 0,
      left: 0,
    });
  }, []);

  const showPostWeld = useMemo(() => {
    const isPostweldExists = !!device?.channelsData?.settingsByMode[CycleSettings.PostWeld].settings;

    const isRunning = device?.channelsData?.iotData.state !== ChannelState.STOPPED;

    return isPostweldExists && isRunning;
  }, [device]);

  const isShowTestButton = useMemo(() => {
    if (device.iotState === ChannelState.RUNNING) return false;
    return device.isChannel && !!testChannel && !!device.itemNumber && !!device.controllerId;
  }, [device.isChannel, testChannel, device.itemNumber, device.controllerId]);

  const handleCurrentClick = useCallback(() => {
    if (!canEditChannels) return;
    if (!device.isChannel || typeof device.current !== 'number' || device.current <= 0) return;
    setCurrentAtClick(device.current);
    setShowCurrentModal(true);
  }, [canEditChannels, device.isChannel, device.current]);

  const handleMeasuredCurrentSend = useCallback(async () => {
    console.log(`Current scaling updated for device ${device.name}`);
  }, []);

  return (
    <tr className={`c-cycle-table__tr-item shadow-inset-table-top hover:bg-stroke ${getBackgroundColor()}`}>
      <RowItem classExtend="pl-2 text-left">
        <p className="inline-block" ref={targetRef} onMouseEnter={handleHover} onMouseLeave={onMouseLeaveHandler}>
          {device.name}
          <sub className="pl-1 text-[#818181] ">({device.controller})</sub>
        </p>
        <ChannelSettings device={device} position={popupPosition} isShow={showPopup} measurement={measurement} />
      </RowItem>
      {/* New inline editable columns placed right after Name */}
      <RowItem classExtend="border-l-2 border-stroke dark:border-strokedark">
        {device.isChannel ? (
          <InlineEditableCell
            mode="checkbox"
            checked={device.output_enabled ?? false}
            canEdit={canEditChannels}
            onToggle={async (checked) => {
              const success = await changeChannelActionWithReturn(
                device.controllerId!,
                Number(device.itemNumber),
                ChangeChannelAction.SET_CHANNEL_OUTPUT_CHANGE,
                checked ? '0' : '1',
              );
              if (success) {
                onDataChange?.(device, { output_enabled: checked });
              }
              return success;
            }}
            value={undefined}
          />
        ) : (
          '-'
        )}
      </RowItem>
      <RowItem>
        {device.isChannel ? (
          <InlineEditableCell<number>
            value={device.Pw}
            canEdit={canEditChannels}
            input={({ value, onChange, disabled }) => (
              <input
                type="number"
                className="w-[34px] text-center"
                value={value ?? ''}
                disabled={disabled}
                min={0}
                max={100}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : Number(e.target.value);
                  if (val !== null && (val < 0 || val > 100)) return;
                  onChange(val);
                }}
              />
            )}
            onSave={async (v) => {
              if (v !== null && (v < 0 || v > 100)) return false;
              const success = await changeChannelActionWithReturn(
                device.controllerId!,
                Number(device.itemNumber),
                ChangeChannelAction.SET_CHANNEL_POWER_SCALE,
                String(v),
              );
              if (success) {
                onDataChange?.(device, { Pw: v });
              }
              return success;
            }}
          />
        ) : (
          '-'
        )}
      </RowItem>
      <RowItem classExtend="border-r-2 border-stroke dark:border-strokedark">
        {device.isChannel ? (
          <InlineEditableCell<number>
            value={device.Tr}
            canEdit={canEditChannels}
            input={({ value, onChange, disabled }) => (
              <input
                type="number"
                className="w-[64px] text-center"
                value={value ?? ''}
                disabled={disabled}
                min={-1500}
                max={1500}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : Number(e.target.value);
                  if (val !== null && (val < -1500 || val > 1500)) return;
                  onChange(val);
                }}
              />
            )}
            onSave={async (v) => {
              if (v !== null && (v < -1500 || v > 1500)) return false;
              const success = await changeChannelActionWithReturn(
                device.controllerId!,
                Number(device.itemNumber),
                ChangeChannelAction.SET_CHANNEL_TRIM_CHANNEL_SETPOINT,
                String(v),
              );
              if (success) {
                onDataChange?.(device, { Tr: v });
              }
              return success;
            }}
          />
        ) : (
          '-'
        )}
      </RowItem>

      <RowItem>
        <p>{typeof device.degrees === 'number' ? getTempByMeasurement(measurement, device.degrees) : device.degrees}</p>
      </RowItem>
      <RowItem>
        <p>{typeof device.sp === 'number' ? getTempByMeasurement(measurement, device.sp) : device.sp}</p>
      </RowItem>
      <RowItem>{<DeltaView value={device.deltaT} currentMeasurement={measurement} />}</RowItem>
      <RowItem>
        <p>{device.present}</p>
      </RowItem>
      <RowItem>
        <p
          className={
            canEditChannels && device.isChannel && typeof device.current === 'number' && device.current > 0
              ? 'cursor-pointer hover:text-primary'
              : ''
          }
          onClick={handleCurrentClick}
        >
          {device.current}
        </p>
      </RowItem>

      <RowItem classExtend="pr-2 flex flex-col">
        <CycleTableItemState
          isChannel={device.isChannel}
          timestamp={device.sk}
          state={device.iotState}
          controllerId={device.controllerId}
          itemNum={device.itemNumber}
          itemDisconnected={device.itemDisconnected}
          controllerDisconnected={device.controllerDisconnected}
          controllerFaulted={controllerFaulted}
          manualFaultMessage={device.manualFaultMessage}
        />
        {controllerFaulted && <sub className="py-1 text-[#818181] ">{t('faults.controllerFaulted')}</sub>}
      </RowItem>
      {showPostWeld && (
        <>
          <RowItem>
            <p>{device.channelsData.settingsByMode[CycleSettings.PostWeld].settings.recipe.currentStep}</p>
          </RowItem>
          <RowItem>
            <p>{device.channelsData.iotData.minutesLeftInStep}</p>
          </RowItem>
        </>
      )}
      {isShowTestButton && (
        <RowItem>
          <Formik initialValues={{ name: '' }} onSubmit={() => testChannel(device.controllerId, device.itemNumber)}>
            {(props) => (
              <Form>
                <InteractiveButton
                  text={'Test'}
                  submit
                  size={ElementSize.XXS}
                  className="px-1.5"
                  isLoading={props.isSubmitting}
                />
              </Form>
            )}
          </Formik>
        </RowItem>
      )}
      <MeasuredCurrentModal
        isVisible={showCurrentModal}
        onClose={() => setShowCurrentModal(false)}
        onSend={handleMeasuredCurrentSend}
        currentAtClick={currentAtClick ?? 0}
        currentScaling={parseFloat(device.channelsData?.moreCHdata?.current_scaling?.value ?? '0')}
        controllerId={device.controllerId!}
        itemNumber={device.itemNumber!}
      />
    </tr>
  );
};

export default CycleTableRow;
